import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { handleApiError } from '@/lib/errorHandler';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ActiveBadge } from '@/components/Badges';
import { EmptyState, LoadingState } from '@/components/States';
import Pagination from '@/components/Pagination';
import { Plus, Search, Edit2, Trash2, Power, Building2, Loader2, Filter, Palette, Mail, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { COMPANY_STATUS_LABEL, BR_STATES } from '@/lib/labels';
import { maskCNPJ, maskPhone, onlyDigits } from '@/lib/format';
import { ColorPicker } from '@/components/ui/color-picker';
import { LogoUploader } from '@/components/ui/logo-uploader';
import { CepInput } from '@/components/ui/cep-input';

const EMPTY_FORM = {
  name: '', code: '', tradeName: '', cnpj: '', email: '', phone: '',
  address: '', number: '', complement: '', city: '', state: '', zipCode: '',
  status: 'ACTIVE', planId: 1, maxUsers: 5, maxTickets: 100,
};

const EMPTY_LAYOUT = {
  customDomain: '',
  companyName: '',
  primaryColor: '#2563EB',
  secondaryColor: '#1E40AF',
  accentColor: '#3B82F6',
  sidebarBgColor: '#1E293B',
  sidebarTextColor: '#F8FAFC',
  logoFile: null,
  faviconFile: null,
  loginTitle: 'Gestão profissional de garantias',
  loginSubtitle: '',
  emailPrimaryColor: '#2563EB',
  emailSecondaryColor: '#1E40AF',
  emailTextColor: '#1F2937',
  emailHeaderLogoUrl: '',
  emailFooterText: '',
  emailSignature: '',
};

const EMPTY_SMTP = {
  smtpHost: '',
  smtpPort: 587,
  smtpUser: '',
  smtpPassword: '',
  smtpSecure: true,
  senderEmail: '',
  senderName: '',
  replyToEmail: '',
  isActive: false,
  sendOnTicketCreated: true,
  sendOnStatusUpdate: true,
  sendOnTicketAssigned: true,
  sendOnTicketFinalized: true,
  sendOnSlaWarning: true,
  sendOnSlaExceeded: true,
};

export default function CompaniesPage() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [layoutForm, setLayoutForm] = useState(EMPTY_LAYOUT);
  const [smtpForm, setSmtpForm] = useState(EMPTY_SMTP);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [plans, setPlans] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, pageSize: 10 };
      if (q) params.q = q;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/companies', { params });
      setItems(data.items);
      setMeta({ page: data.page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages });
    } catch (e) {
      toast.error('Falha ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => { api.get('/plans').then(({ data }) => setPlans(data.items)).catch(() => {}); }, []);

  const openCreate = () => { 
    setEditing(null); 
    setForm(EMPTY_FORM); 
    setLayoutForm(EMPTY_LAYOUT);
    setSmtpForm(EMPTY_SMTP);
    setTestEmail('');
    setOpenModal(true); 
  };
  
  const handleAddressFound = (addressData) => {
    setForm(prev => ({
      ...prev,
      zipCode: addressData.zipCode,
      address: addressData.address,
      city: addressData.city,
      state: addressData.state,
    }));
  };

  const openEdit = async (c) => {
    setEditing(c);
    setForm({ ...EMPTY_FORM, ...c, cnpj: maskCNPJ(c.cnpj), phone: maskPhone(c.phone) });
    setTestEmail(c.email || '');
    
    // Carregar layout da empresa
    try {
      const { data } = await api.get(`/companies/${c.id}/layout`);
      if (data) {
        setLayoutForm({ 
          ...EMPTY_LAYOUT, 
          ...data, 
          logoFile: null, 
          faviconFile: null,
          companyName: data.companyName || c.name // Garantir que sempre tenha nome
        });
      } else {
        setLayoutForm({ ...EMPTY_LAYOUT, companyName: c.name || c.tradeName || 'Empresa' });
      }
    } catch (e) {
      // Se não existir layout, usar valores padrão
      setLayoutForm({ ...EMPTY_LAYOUT, companyName: c.name || c.tradeName || 'Empresa' });
    }
    
    // Carregar configurações SMTP
    try {
      const { data } = await api.get('/email-settings');
      if (data) {
        setSmtpForm({ ...EMPTY_SMTP, ...data });
      } else {
        setSmtpForm(EMPTY_SMTP);
      }
    } catch (e) {
      setSmtpForm(EMPTY_SMTP);
    }
    
    setOpenModal(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Validar campos obrigatórios
      if (!form.name || !form.code || !form.cnpj || !form.email) {
        toast.error('Preencha todos os campos obrigatórios');
        setSaving(false);
        return;
      }

      // Preparar payload
      const cnpjDigits = onlyDigits(form.cnpj);
      
      const payload = { 
        name: form.name,
        code: form.code,
        tradeName: form.tradeName || undefined,
        email: form.email,
        phone: form.phone ? onlyDigits(form.phone) : undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zipCode: form.zipCode ? onlyDigits(form.zipCode) : undefined,
        isActive: form.status === 'ACTIVE',
        planId: Number(form.planId),
        maxUsers: Number(form.maxUsers),
        maxTickets: Number(form.maxTickets),
      };

      // Só enviar CNPJ se for válido (14 dígitos)
      if (cnpjDigits && cnpjDigits.length === 14) {
        payload.cnpj = cnpjDigits;
      }
      
      let companyId;
      
      console.log('Salvando empresa com payload:', payload);
      console.log('CNPJ original:', form.cnpj);
      console.log('CNPJ sem dígitos:', onlyDigits(form.cnpj));
      console.log('Editing:', editing);
      
      if (editing) {
        console.log('Fazendo PATCH para:', `/companies/${editing.id}`);
        await api.patch(`/companies/${editing.id}`, payload);
        companyId = editing.id;
        toast.success('Empresa atualizada');
      } else {
        const { data } = await api.post('/companies', payload);
        companyId = data.id;
        toast.success('Empresa criada');
      }
      
      // Salvar layout (se houver alterações)
      if (companyId && editing && (layoutForm.customDomain || layoutForm.companyName || layoutForm.logoFile || layoutForm.faviconFile)) {
        try {
          const formData = new FormData();
          
          // companyName é obrigatório
          const companyNameToSave = layoutForm.companyName || form.name || 'Empresa';
          formData.append('companyName', companyNameToSave);
          
          // Adicionar campos de texto opcionais
          if (layoutForm.customDomain) formData.append('customDomain', layoutForm.customDomain);
          if (layoutForm.primaryColor) formData.append('primaryColor', layoutForm.primaryColor);
          if (layoutForm.secondaryColor) formData.append('secondaryColor', layoutForm.secondaryColor);
          if (layoutForm.accentColor) formData.append('accentColor', layoutForm.accentColor);
          if (layoutForm.sidebarBgColor) formData.append('sidebarBgColor', layoutForm.sidebarBgColor);
          if (layoutForm.sidebarTextColor) formData.append('sidebarTextColor', layoutForm.sidebarTextColor);
          if (layoutForm.loginTitle) formData.append('loginTitle', layoutForm.loginTitle);
          if (layoutForm.loginSubtitle) formData.append('loginSubtitle', layoutForm.loginSubtitle);
          
          // Campos de email
          if (layoutForm.emailPrimaryColor) formData.append('emailPrimaryColor', layoutForm.emailPrimaryColor);
          if (layoutForm.emailSecondaryColor) formData.append('emailSecondaryColor', layoutForm.emailSecondaryColor);
          if (layoutForm.emailTextColor) formData.append('emailTextColor', layoutForm.emailTextColor);
          if (layoutForm.emailFooterText) formData.append('emailFooterText', layoutForm.emailFooterText);
          if (layoutForm.emailSignature) formData.append('emailSignature', layoutForm.emailSignature);
          
          // Adicionar arquivos
          if (layoutForm.logoFile instanceof File) {
            formData.append('logoFile', layoutForm.logoFile);
          }
          if (layoutForm.faviconFile instanceof File) {
            formData.append('faviconFile', layoutForm.faviconFile);
          }
          
          console.log('Salvando layout para empresa:', companyId);
          
          await api.post(`/companies/${companyId}/layout`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          toast.success('Layout configurado com sucesso!');
        } catch (layoutError) {
          console.error('Erro ao salvar layout:', layoutError);
          console.error('Detalhes do erro:', layoutError.response?.data);
          toast.error('Empresa salva, mas erro ao configurar layout: ' + (layoutError.response?.data?.message || layoutError.message));
        }
      }
      
      setOpenModal(false);
      load(meta.page);
    } catch (e) {
      handleApiError(e, 'Erro ao salvar empresa');
    } finally {
      setSaving(false);
    }
  };

  const saveSmtp = async () => {
    if (!editing) {
      toast.error('Salve a empresa primeiro');
      return;
    }
    
    try {
      await api.patch('/email-settings', smtpForm);
      toast.success('Configurações SMTP salvas!');
    } catch (e) {
      handleApiError(e, 'Erro ao salvar SMTP');
    }
  };

  const testSmtpConnection = async () => {
    setTestingSmtp(true);
    try {
      const { data } = await api.post('/email-settings/test-connection');
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      handleApiError(e, 'Erro ao testar conexão');
    } finally {
      setTestingSmtp(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Digite um email para teste');
      return;
    }
    
    setSendingTest(true);
    try {
      const { data } = await api.post(`/email-settings/send-test?email=${encodeURIComponent(testEmail)}`);
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      handleApiError(e, 'Erro ao enviar email de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/companies/${c.id}/toggle-active`);
      toast.success(c.isActive ? 'Empresa desativada' : 'Empresa ativada');
      load(meta.page);
    } catch { toast.error('Erro ao alterar status'); }
  };
  const remove = async () => {
    try { await api.delete(`/companies/${confirmDel.id}`); toast.success('Empresa excluída'); setConfirmDel(null); load(meta.page); }
    catch { toast.error('Erro ao excluir'); }
  };

  if (!hasRole('SUPER_ADMIN')) {
    return <div className="text-slate-600">Apenas Super Admin pode acessar.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-blue-600" /> Empresas</h1>
          <p className="text-sm text-slate-500">Gerencie as empresas (tenants) do sistema.</p>
        </div>
        <Button data-testid="new-company-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Nova Empresa
        </Button>
      </div>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="company-search" placeholder="Buscar por nome, CNPJ, código..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {Object.entries(COMPANY_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? <LoadingState /> : items.length === 0 ? (
          <EmptyState title="Nenhuma empresa" description="Crie sua primeira empresa." icon={Building2} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">CNPJ</th>
                  <th className="px-4 py-3 font-medium">Plano</th>
                  <th className="px-4 py-3 font-medium">Usuários</th>
                  <th className="px-4 py-3 font-medium">Tickets</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.code}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{maskCNPJ(c.cnpj)}</td>
                    <td className="px-4 py-3 text-slate-700">{plans.find(p => p.id === c.planId)?.name || c.planId}</td>
                    <td className="px-4 py-3 text-slate-700">{c.userCount ?? 0} / {c.maxUsers}</td>
                    <td className="px-4 py-3 text-slate-700">{c.ticketCount ?? 0} / {c.maxTickets}</td>
                    <td className="px-4 py-3"><ActiveBadge active={c.isActive} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Editar"><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => toggleActive(c)} title={c.isActive ? 'Desativar' : 'Ativar'}><Power className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(c)} title="Excluir" className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination {...meta} onPageChange={(p) => load(p)} />
      </Card>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="basic">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">Dados Básicos</TabsTrigger>
              <TabsTrigger value="address">Endereço</TabsTrigger>
              <TabsTrigger value="plan">Plano</TabsTrigger>
              <TabsTrigger value="layout" disabled={!editing}><Palette className="w-4 h-4 mr-1" /> Layout</TabsTrigger>
              <TabsTrigger value="smtp" disabled={!editing}><Mail className="w-4 h-4 mr-1" /> SMTP</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Código *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
              </div>
              <div><Label>Nome Fantasia</Label><Input value={form.tradeName} onChange={(e) => setForm({ ...form, tradeName: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>CNPJ *</Label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: maskCNPJ(e.target.value) })} placeholder="00.000.000/0000-00" /></div>
                <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} placeholder="(11) 99999-9999" /></div>
              </div>
              <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </TabsContent>
            <TabsContent value="address" className="space-y-3 pt-3">
              <div className="grid grid-cols-3 gap-3">
                <CepInput
                  value={form.zipCode}
                  onChange={(value) => setForm({ ...form, zipCode: value })}
                  onAddressFound={handleAddressFound}
                />
                <div className="col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rua, Avenida..." /></div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div><Label>Número</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="123" /></div>
                <div className="col-span-3"><Label>Complemento</Label><Input value={form.complement} onChange={(e) => setForm({ ...form, complement: e.target.value })} placeholder="Sala, Andar, Bloco..." /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label>Cidade</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Cidade" /></div>
                <div><Label>UF</Label>
                  <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                    <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{BR_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="plan" className="space-y-3 pt-3">
              <div><Label>Plano *</Label>
                <Select value={String(form.planId)} onValueChange={(v) => setForm({ ...form, planId: Number(v) })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{plans.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Máx. Usuários *</Label><Input type="number" value={form.maxUsers} onChange={(e) => setForm({ ...form, maxUsers: e.target.value })} /></div>
                <div><Label>Máx. Tickets *</Label><Input type="number" value={form.maxTickets} onChange={(e) => setForm({ ...form, maxTickets: e.target.value })} /></div>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(COMPANY_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent value="layout" className="space-y-4 pt-3 max-h-[500px] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-800">
                  <strong>White Label:</strong> Configure a aparência personalizada do sistema para esta empresa.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">URL Personalizada</h4>
                <div>
                  <Label>Subdomínio *</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={layoutForm.customDomain} 
                      onChange={(e) => setLayoutForm({ ...layoutForm, customDomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="empresa1"
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-600">.algti.com</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    URL de acesso: <strong>{layoutForm.customDomain || 'empresa'}.algti.com</strong>
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Identidade Visual</h4>
                <div>
                  <Label>Nome da Empresa (Menu Lateral)</Label>
                  <Input 
                    value={layoutForm.companyName} 
                    onChange={(e) => setLayoutForm({ ...layoutForm, companyName: e.target.value })}
                    placeholder="Nome que aparece no menu"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <LogoUploader
                    label="Logo (Menu Lateral)"
                    value={layoutForm.logoUrl}
                    onChange={(file) => setLayoutForm({ ...layoutForm, logoFile: file })}
                  />
                  <LogoUploader
                    label="Favicon"
                    value={layoutForm.faviconUrl}
                    onChange={(file) => setLayoutForm({ ...layoutForm, faviconFile: file })}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Cores do Sistema</h4>
                <div className="grid grid-cols-2 gap-3">
                  <ColorPicker
                    label="Cor Primária"
                    value={layoutForm.primaryColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, primaryColor: color })}
                  />
                  <ColorPicker
                    label="Cor Secundária"
                    value={layoutForm.secondaryColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, secondaryColor: color })}
                  />
                  <ColorPicker
                    label="Cor de Destaque"
                    value={layoutForm.accentColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, accentColor: color })}
                  />
                  <ColorPicker
                    label="Fundo do Menu"
                    value={layoutForm.sidebarBgColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, sidebarBgColor: color })}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Página de Login</h4>
                <div>
                  <Label>Título</Label>
                  <Input 
                    value={layoutForm.loginTitle} 
                    onChange={(e) => setLayoutForm({ ...layoutForm, loginTitle: e.target.value })}
                    placeholder="Gestão profissional de garantias"
                  />
                  <p className="text-xs text-slate-500 mt-1">Texto fixo "Gestão de Garantia" sempre aparece</p>
                </div>
                <div>
                  <Label>Subtítulo (Opcional)</Label>
                  <Input 
                    value={layoutForm.loginSubtitle} 
                    onChange={(e) => setLayoutForm({ ...layoutForm, loginSubtitle: e.target.value })}
                    placeholder="Texto adicional..."
                  />
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-slate-700 mb-2">Preview das Cores:</h5>
                <div className="flex gap-2">
                  <div className="flex-1 h-12 rounded flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: layoutForm.primaryColor }}>
                    Primária
                  </div>
                  <div className="flex-1 h-12 rounded flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: layoutForm.secondaryColor }}>
                    Secundária
                  </div>
                  <div className="flex-1 h-12 rounded flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: layoutForm.accentColor }}>
                    Destaque
                  </div>
                  <div className="flex-1 h-12 rounded flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: layoutForm.sidebarBgColor }}>
                    Menu
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Layout de Emails
                </h4>
                <p className="text-xs text-slate-500">Configure a aparência dos emails automáticos enviados pelo sistema.</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <ColorPicker
                    label="Cor Primária (Email)"
                    value={layoutForm.emailPrimaryColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, emailPrimaryColor: color })}
                  />
                  <ColorPicker
                    label="Cor Secundária (Email)"
                    value={layoutForm.emailSecondaryColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, emailSecondaryColor: color })}
                  />
                  <ColorPicker
                    label="Cor do Texto (Email)"
                    value={layoutForm.emailTextColor}
                    onChange={(color) => setLayoutForm({ ...layoutForm, emailTextColor: color })}
                  />
                </div>

                <div>
                  <Label>Texto do Rodapé (Email)</Label>
                  <Input 
                    value={layoutForm.emailFooterText} 
                    onChange={(e) => setLayoutForm({ ...layoutForm, emailFooterText: e.target.value })}
                    placeholder="© 2026 Sua Empresa. Todos os direitos reservados."
                  />
                </div>

                <div>
                  <Label>Assinatura (Email)</Label>
                  <Input 
                    value={layoutForm.emailSignature} 
                    onChange={(e) => setLayoutForm({ ...layoutForm, emailSignature: e.target.value })}
                    placeholder="Equipe de Suporte"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="smtp" className="space-y-4 pt-3 max-h-[500px] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-800">
                  <strong>Configuração SMTP:</strong> Configure o servidor de email para envio automático de notificações.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Servidor SMTP</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Host SMTP *</Label>
                    <Input 
                      value={smtpForm.smtpHost} 
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpHost: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <Label>Porta *</Label>
                    <Input 
                      type="number"
                      value={smtpForm.smtpPort} 
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpPort: Number(e.target.value) })}
                      placeholder="587"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Usuário SMTP *</Label>
                    <Input 
                      value={smtpForm.smtpUser} 
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpUser: e.target.value })}
                      placeholder="seu-email@gmail.com"
                    />
                  </div>
                  <div>
                    <Label>Senha SMTP *</Label>
                    <Input 
                      type="password"
                      value={smtpForm.smtpPassword} 
                      onChange={(e) => setSmtpForm({ ...smtpForm, smtpPassword: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox"
                    id="smtpSecure"
                    checked={smtpForm.smtpSecure}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtpSecure: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="smtpSecure" className="cursor-pointer">Usar TLS/SSL (recomendado)</Label>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Remetente</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email Remetente *</Label>
                    <Input 
                      type="email"
                      value={smtpForm.senderEmail} 
                      onChange={(e) => setSmtpForm({ ...smtpForm, senderEmail: e.target.value })}
                      placeholder="noreply@suaempresa.com"
                    />
                  </div>
                  <div>
                    <Label>Nome Remetente *</Label>
                    <Input 
                      value={smtpForm.senderName} 
                      onChange={(e) => setSmtpForm({ ...smtpForm, senderName: e.target.value })}
                      placeholder="Sua Empresa"
                    />
                  </div>
                </div>

                <div>
                  <Label>Email para Resposta (Opcional)</Label>
                  <Input 
                    type="email"
                    value={smtpForm.replyToEmail} 
                    onChange={(e) => setSmtpForm({ ...smtpForm, replyToEmail: e.target.value })}
                    placeholder="suporte@suaempresa.com"
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Eventos de Envio</h4>
                <p className="text-xs text-slate-500">Selecione quando os emails devem ser enviados automaticamente:</p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="sendOnTicketCreated"
                      checked={smtpForm.sendOnTicketCreated}
                      onChange={(e) => setSmtpForm({ ...smtpForm, sendOnTicketCreated: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="sendOnTicketCreated" className="cursor-pointer">Ao criar ticket (para cliente)</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="sendOnStatusUpdate"
                      checked={smtpForm.sendOnStatusUpdate}
                      onChange={(e) => setSmtpForm({ ...smtpForm, sendOnStatusUpdate: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="sendOnStatusUpdate" className="cursor-pointer">Ao atualizar status (para cliente)</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="sendOnTicketAssigned"
                      checked={smtpForm.sendOnTicketAssigned}
                      onChange={(e) => setSmtpForm({ ...smtpForm, sendOnTicketAssigned: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="sendOnTicketAssigned" className="cursor-pointer">Ao atribuir ticket (para atendente)</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="sendOnTicketFinalized"
                      checked={smtpForm.sendOnTicketFinalized}
                      onChange={(e) => setSmtpForm({ ...smtpForm, sendOnTicketFinalized: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="sendOnTicketFinalized" className="cursor-pointer">Ao finalizar ticket (para cliente)</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="sendOnSlaWarning"
                      checked={smtpForm.sendOnSlaWarning}
                      onChange={(e) => setSmtpForm({ ...smtpForm, sendOnSlaWarning: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="sendOnSlaWarning" className="cursor-pointer">Alerta de SLA próximo (para atendente)</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="sendOnSlaExceeded"
                      checked={smtpForm.sendOnSlaExceeded}
                      onChange={(e) => setSmtpForm({ ...smtpForm, sendOnSlaExceeded: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="sendOnSlaExceeded" className="cursor-pointer">SLA vencido (para gestor)</Label>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Testes</h4>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={testSmtpConnection}
                    disabled={testingSmtp}
                    className="flex-1"
                  >
                    {testingSmtp ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testando...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Testar Conexão</>}
                  </Button>
                  <Button 
                    type="button"
                    onClick={saveSmtp}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Salvar SMTP
                  </Button>
                </div>

                <div>
                  <Label>Enviar Email de Teste</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="email"
                      value={testEmail} 
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="seu-email@exemplo.com"
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      onClick={sendTestEmail}
                      disabled={sendingTest}
                    >
                      {sendingTest ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> Enviar</>}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox"
                    id="isActive"
                    checked={smtpForm.isActive}
                    onChange={(e) => setSmtpForm({ ...smtpForm, isActive: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="isActive" className="cursor-pointer font-semibold">Ativar envio automático de emails</Label>
                </div>
                <p className="text-xs text-slate-500 mt-1">Quando ativado, os emails serão enviados automaticamente conforme os eventos selecionados acima.</p>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Excluir empresa?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Esta ação irá desativar a empresa <span className="font-semibold">{confirmDel?.name}</span>. Você pode reativá-la depois.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
